// ─────────────────────────────────────────────
//  SoloReliefFlow — after a SOLO relief is saved (one MD now covers the room
//  alone), this asks where the relieved provider goes:
//
//    • New Room    → pick any of today's rooms; they become its primary CRNA
//                    (replacing whoever is there, like a takeover).
//    • Home        → marked departed for the day (Home section).
//    • Travel to Another Facility → transfer doc + departed here; appears in
//                    the Traveling section until Arrival at the destination.
//    • Unassigned  → nothing else to do; they're already off the room and
//                    show up on the Unassigned bench.
//
//  Mirrors BenchAssignFlow's visuals/mechanics so the two flows feel the same.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../context/ToastContext";
import {
  updateAssignmentRow,
  setRoomAssignment,
  setRosterMemberDeparted,
  createTransfer,
} from "../firebase/firestore";
import { useFacilities } from "../hooks/useFacilities";
import { resolveFacilityStatus } from "../domain/facilityRules";
import { facilityLabel } from "../domain/facilities";
import tokens from "../theme/tokens";

const { color, radius, shadow, font, space } = tokens;

const roomForRow = (rooms, row) =>
  rooms.find((r) => r.name === row.orRoom || r.name.replace(/^OR\s+/i, "") === row.orRoom) || null;

export default function SoloReliefFlow({ provider, fromRoomName, rooms = [], assignments = [], facilityId, date, updatedBy = null, onClose }) {
  const toast = useToast();
  const [step, setStep] = useState("choose"); // choose | newroom | transfer
  const [busy, setBusy] = useState(false);
  const allFacilities = useFacilities();
  const transferTargets = allFacilities.filter(
    (f) => f.id !== facilityId && resolveFacilityStatus(f) === "active"
  );
  const labelOf = (f) => {
    const known = facilityLabel(f.id);
    return known !== f.id ? known : f.label || f.name || f.id;
  };

  if (!provider) return null;

  const sendHome = async () => {
    setBusy(true);
    try {
      await setRosterMemberDeparted(facilityId, date, provider.id, true, updatedBy);
      toast.success(`${provider.name} sent home for the day.`);
      onClose();
    } catch (e) {
      console.error("Send home failed:", e);
      toast.error("Could not send them home.");
      setBusy(false);
    }
  };

  const doTransfer = async (dest) => {
    setBusy(true);
    try {
      await createTransfer({
        providerId: provider.id,
        providerName: provider.name,
        subRole: provider.subRole || "crna",
        fromFacilityId: facilityId,
        toFacilityId: dest.id,
        toFacilityName: labelOf(dest),
        date,
        createdBy: updatedBy,
      });
      await setRosterMemberDeparted(facilityId, date, provider.id, true, updatedBy);
      toast.success(`${provider.name} is traveling to ${labelOf(dest)} — Arrival confirms when they get there.`);
      onClose();
    } catch (e) {
      console.error("Transfer failed:", e);
      toast.error("Could not start the transfer.");
      setBusy(false);
    }
  };

  // Every OTHER room row on today's board — with or without a current CRNA.
  const targetRows = assignments
    .filter((a) => a.id && a.orRoom && a.ahpId !== provider.id)
    .slice()
    .sort((a, b) => (a.orRoom || "").localeCompare(b.orRoom || "", undefined, { numeric: true }));

  const moveToRoom = async (row) => {
    setBusy(true);
    try {
      const room = roomForRow(rooms, row);
      await updateAssignmentRow(row.id, {
        ahpId: provider.id,
        ahpName: provider.name,
        ahpShiftCode: provider.shiftCode ?? null,
        ahpDepartTime: "",
      });
      if (room) {
        await setRoomAssignment(room.id, {
          providerId: provider.id,
          providerName: provider.name,
          providerRole: (provider.subRole || "crna").toUpperCase(),
        });
      }
      toast.success(`${provider.name} moved to ${row.orRoom}.`);
      onClose();
    } catch (e) {
      console.error("Move to room failed:", e);
      toast.error("Could not move them to that room.");
      setBusy(false);
    }
  };

  // PORTAL to <body> (same as Modal): this flow mounts inside a RoomCard, and
  // in TV mode the board sits under a `transform: scale(...)` — a transformed
  // ancestor becomes the containing block for position:fixed, which trapped
  // and clipped this overlay inside the card's grid slot. The portal escapes.
  return createPortal(
    <div style={st.overlay} role="dialog" aria-modal="true" aria-label={`Where does ${provider.name} go?`}>
      <div style={st.modal}>
        {step === "choose" && (
          <>
            <div style={st.title}>Where does {provider.name} go?</div>
            <div style={st.sub}>
              {fromRoomName ? `${fromRoomName} is now covered solo by its MD. ` : ""}The relieved provider needs a destination.
            </div>
            <div style={st.choiceCol}>
              <button style={st.choice} disabled={busy} onClick={() => setStep("newroom")}>
                <span style={st.choiceIcon}>🚪</span>
                <span><span style={st.choiceLabel}>New Room</span><span style={st.choiceSub}>Place them in another room right away.</span></span>
              </button>
              <button style={st.choice} disabled={busy} onClick={sendHome}>
                <span style={st.choiceIcon}>🏠</span>
                <span><span style={st.choiceLabel}>Home</span><span style={st.choiceSub}>Done for the day — moves to the Home section.</span></span>
              </button>
              {transferTargets.length > 0 && (
                <button style={st.choice} disabled={busy} onClick={() => setStep("transfer")}>
                  <span style={st.choiceIcon}>🚗</span>
                  <span><span style={st.choiceLabel}>Travel to Another Facility</span><span style={st.choiceSub}>Tracked in the Traveling section until Arrival.</span></span>
                </button>
              )}
              <button style={st.choice} disabled={busy} onClick={onClose}>
                <span style={st.choiceIcon}>🪑</span>
                <span><span style={st.choiceLabel}>Unassigned</span><span style={st.choiceSub}>Leave them on the Unassigned bench for now.</span></span>
              </button>
            </div>
          </>
        )}

        {step === "newroom" && (
          <>
            <div style={st.title}>New Room — pick where {provider.name} goes</div>
            <div style={st.sub}>They become that room's primary CRNA (replacing the current one, if any).</div>
            <div style={st.rowList}>
              {targetRows.length === 0 ? (
                <div style={st.empty}>No other rooms on today's board.</div>
              ) : (
                targetRows.map((row) => (
                  <button key={row.id} style={st.row} disabled={busy} onClick={() => moveToRoom(row)}>
                    <span style={st.rowRoom}>{row.orRoom}</span>
                    <span style={st.rowWho}>{row.ahpName ? `${row.ahpName} →` : "no CRNA"}</span>
                  </button>
                ))
              )}
            </div>
            <div style={st.actions}>
              <button style={st.ghost} onClick={() => setStep("choose")} disabled={busy}>← Back</button>
            </div>
          </>
        )}

        {step === "transfer" && (
          <>
            <div style={st.title}>Travel — pick the destination</div>
            <div style={st.sub}>{provider.name} leaves this facility and appears as Traveling until Arrival is pressed.</div>
            <div style={st.choiceCol}>
              {transferTargets.map((f) => (
                <button key={f.id} style={st.choice} disabled={busy} onClick={() => doTransfer(f)}>
                  <span style={st.choiceIcon}>🏥</span>
                  <span><span style={st.choiceLabel}>{labelOf(f)}</span></span>
                </button>
              ))}
            </div>
            <div style={st.actions}>
              <button style={st.ghost} onClick={() => setStep("choose")} disabled={busy}>← Back</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

const st = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.40)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 250, padding: 16 },
  modal: { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.lg, borderRadius: radius.lg, padding: 24, width: 420, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", fontFamily: font.family },
  title: { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary, marginBottom: 4 },
  sub: { fontSize: 13, color: color.text.muted, marginBottom: 18 },
  choiceCol: { display: "flex", flexDirection: "column", gap: space.sm },
  choice: { display: "flex", alignItems: "center", gap: space.md, textAlign: "left", width: "100%", background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.md, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit" },
  choiceIcon: { fontSize: 24, flexShrink: 0 },
  choiceLabel: { display: "block", fontSize: 15, fontWeight: font.weight.bold, color: color.text.primary },
  choiceSub: { display: "block", fontSize: font.size.label, color: color.text.muted, marginTop: 2 },
  rowList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.md, width: "100%", background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit" },
  rowRoom: { fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary },
  rowWho: { fontSize: font.size.label, color: color.text.muted },
  empty: { fontSize: 13, color: color.text.muted, padding: "12px 4px" },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 },
  ghost: { background: "transparent", border: `1px solid ${color.border.default}`, color: color.text.secondary, borderRadius: radius.sm, padding: "8px 14px", fontSize: 13, cursor: "pointer" },
};
